/**
 * AforoParser.ts — Extractor de aforo/capacidad declarado en documentos PTEL
 *
 * Parsea valores de aforo que los técnicos municipales incluyen en las tablas
 * de infraestructuras: nombre, dirección, observaciones.
 *
 * Patrones reconocidos:
 *   - Explícitos: "Aforo: 200 personas", "Capacidad: 500 plazas"
 *   - Unitarios: "200 personas", "100 camas", "50 alumnos"
 *   - Superficie: "500 m²" (para estimación CTE DB-SI posterior)
 *   - Compuestos: "2 plantas × 100 personas", "3 aulas × 25 alumnos"
 *
 * @module services/parser
 * @version 1.0.0
 * @task F7B.03
 */

// ============================================================================
// TIPOS
// ============================================================================

/** Unidad de medida del aforo */
export type AforoUnit =
  | 'personas'
  | 'plazas'
  | 'camas'
  | 'alumnos'
  | 'localidades'   // "200 localidades" (recintos)
  | 'vehículos'
  | 'm²'
  | 'unidad';       // Número suelto sin unidad explícita

/** Método de obtención del aforo */
export type AforoMethod =
  | 'DECLARADO'     // Extraído literalmente del documento
  | 'ESTIMADO';     // Calculado (futuro: CTE DB-SI en F7B.02)

/** Confianza del resultado */
export type AforoConfidence = 'ALTA' | 'MEDIA' | 'BAJA';

/** Resultado de extracción de aforo */
export interface AforoResult {
  /** Valor numérico del aforo */
  value: number;
  /** Unidad de medida */
  unit: AforoUnit;
  /** Texto original que generó la extracción */
  sourceText: string;
  /** Campo origen: nombre, dirección u observaciones */
  sourceField: 'nombre' | 'direccion' | 'observaciones';
  /** Método de obtención */
  method: AforoMethod;
  /** Confianza */
  confidence: AforoConfidence;
}

// ============================================================================
// PATRONES DE EXTRACCIÓN
// ============================================================================

/**
 * Mapa de sinónimos → unidad canónica.
 * Orden: longest-match-first para evitar colisiones parciales.
 */
const UNIT_MAP: ReadonlyArray<[RegExp, AforoUnit]> = [
  [/personas?/i, 'personas'],
  [/plazas?/i, 'plazas'],
  [/camas?/i, 'camas'],
  [/alumnos?/i, 'alumnos'],
  [/localidades?/i, 'localidades'],
  [/veh[ií]culos?/i, 'vehículos'],
  [/butacas?/i, 'localidades'],    // butaca → localidades (recintos)
  [/asientos?/i, 'localidades'],
  [/espectadores?/i, 'personas'],
  [/usuarios?/i, 'personas'],
  [/ocupantes?/i, 'personas'],
  [/pax/i, 'personas'],
  [/pers\.?/i, 'personas'],
  [/plz\.?/i, 'plazas'],
  [/m[²2]/i, 'm²'],
  [/metros?\s*cuadrados?/i, 'm²'],
];

/**
 * Palabras clave que preceden al número.
 * Capturan "Aforo: 200", "Capacidad: 500 personas", "Cap. 150", etc.
 */
const KEYWORD_PREFIX = /(?:aforo|capacidad|cap\.|ocupaci[oó]n|cabida)\s*[:=]?\s*/i;

/**
 * Patrón para número con posible separador de miles español.
 * Captura: "200", "1.500", "2,5" (decimal), "1500"
 */
const NUMBER_PATTERN = /(\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:,\d+)?)/;

/**
 * Patrón compuesto: "2 plantas × 100 personas" → 200
 */
const COMPOUND_PATTERN =
  /(\d+)\s*(?:plantas?|pisos?|aulas?|salas?|naves?|módulos?)\s*[x×*]\s*(\d+)\s*/i;

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Convierte cadena numérica española a número.
 *   "1.500" → 1500
 *   "2,5"   → 2.5
 *   "200"   → 200
 */
function parseSpanishNumber(raw: string): number {
  // Punto como miles, coma como decimal
  const cleaned = raw.replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned);
}

/**
 * Identifica la unidad en el texto inmediatamente posterior al número.
 */
function detectUnit(textAfterNumber: string): AforoUnit {
  const trimmed = textAfterNumber.trim().slice(0, 30); // Solo primeros 30 chars
  for (const [regex, unit] of UNIT_MAP) {
    if (regex.test(trimmed)) return unit;
  }
  return 'unidad';
}

/**
 * Determina confianza según el patrón encontrado.
 */
function computeConfidence(hasKeyword: boolean, unit: AforoUnit): AforoConfidence {
  if (hasKeyword && unit !== 'unidad') return 'ALTA';
  if (hasKeyword || unit !== 'unidad') return 'MEDIA';
  return 'BAJA';
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Extrae aforo declarado de un texto libre.
 *
 * Estrategia:
 *   1. Buscar patrón con keyword ("Aforo: 200 personas") → ALTA
 *   2. Buscar compuesto ("2 plantas × 100 personas") → MEDIA
 *   3. Buscar número + unidad relevante ("200 personas") → MEDIA
 *
 * @returns Primer resultado con mayor confianza, o null si no se detecta.
 */
export function extractAforo(
  text: string,
  field: AforoResult['sourceField'] = 'observaciones'
): AforoResult | null {
  if (!text || text.trim().length < 2) return null;

  // --- 1) Keyword + número + unidad opcional ---
  const kwRegex = new RegExp(
    KEYWORD_PREFIX.source + NUMBER_PATTERN.source + /\s*(\S+)?/.source,
    'gi'
  );
  let match = kwRegex.exec(text);
  if (match) {
    const value = parseSpanishNumber(match[1]);
    if (value > 0 && value <= 100_000) {
      const explicitUnit = match[2] ? detectUnit(match[2]) : 'unidad';
      const unit: AforoUnit = explicitUnit === 'unidad' ? 'personas' : explicitUnit;
      return {
        value,
        unit,
        sourceText: match[0].trim(),
        sourceField: field,
        method: 'DECLARADO',
        confidence: computeConfidence(true, explicitUnit), // explicitUnit para confianza
      };
    }
  }

  // --- 2) Patrón compuesto: "N unidades × M personas" ---
  const compoundMatch = COMPOUND_PATTERN.exec(text);
  if (compoundMatch) {
    const multiplier = parseInt(compoundMatch[1], 10);
    const perUnit = parseInt(compoundMatch[2], 10);
    const product = multiplier * perUnit;
    if (product > 0 && product <= 100_000) {
      // Buscar unidad tras el segundo número
      const afterIdx = (compoundMatch.index ?? 0) + compoundMatch[0].length;
      const unitText = text.slice(afterIdx);
      const unit = detectUnit(unitText);
      return {
        value: product,
        unit: unit !== 'unidad' ? unit : 'personas',
        sourceText: compoundMatch[0].trim(),
        sourceField: field,
        method: 'DECLARADO',
        confidence: 'MEDIA',
      };
    }
  }

  // --- 3) Número + unidad relevante (sin keyword) ---
  // Solo cuando hay unidad explícita para evitar falsos positivos con coords, teléfonos, etc.
  const unitRegex = new RegExp(
    NUMBER_PATTERN.source + /\s+/.source +
    `(${UNIT_MAP.map(([r]) => r.source).join('|')})`,
    'gi'
  );
  match = unitRegex.exec(text);
  if (match) {
    const value = parseSpanishNumber(match[1]);
    if (value > 0 && value <= 100_000) {
      const unit = detectUnit(match[2]);
      return {
        value,
        unit,
        sourceText: match[0].trim(),
        sourceField: field,
        method: 'DECLARADO',
        confidence: computeConfidence(false, unit),
      };
    }
  }

  return null;
}

/**
 * Extrae aforo buscando en múltiples campos de una infraestructura.
 * Prioridad: observaciones > nombre > dirección.
 */
export function extractAforoFromInfrastructure(fields: {
  nombre?: string;
  direccion?: string;
  observaciones?: string;
}): AforoResult | null {
  // Orden de prioridad: observaciones primero (más probable que contenga aforo)
  const sources: Array<[string | undefined, AforoResult['sourceField']]> = [
    [fields.observaciones, 'observaciones'],
    [fields.nombre, 'nombre'],
    [fields.direccion, 'direccion'],
  ];

  let bestResult: AforoResult | null = null;
  const confidenceOrder: Record<AforoConfidence, number> = {
    ALTA: 3, MEDIA: 2, BAJA: 1,
  };

  for (const [text, field] of sources) {
    if (!text) continue;
    const result = extractAforo(text, field);
    if (!result) continue;

    if (
      !bestResult ||
      confidenceOrder[result.confidence] > confidenceOrder[bestResult.confidence]
    ) {
      bestResult = result;
    }

    // ALTA → no seguir buscando
    if (bestResult.confidence === 'ALTA') break;
  }

  return bestResult;
}
